import type { PageContext } from 'vike/types'
import { usePageContext } from 'vike-vue/usePageContext'
import qs from 'qs'

export interface UtmParams {
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_term?: string
  utm_content?: string
}

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const
const STORAGE_KEY = 'or_utm'

function readFromUrl(pageContext: PageContext): UtmParams {
  const query = qs.parse(pageContext.urlOriginal.split('?')[1] || '')
  const params: UtmParams = {}
  for (const key of UTM_KEYS) {
    if (typeof query[key] === 'string') params[key] = query[key] as string
  }
  return params
}

// Landing UTM params only survive on the URL of the entry page — this persists the
// first-touch values for the rest of the browser session so a later action on the
// same visit (e.g. submitting the contact form) can still be attributed to the
// campaign that brought the visitor here, even if they've since navigated client-side.
//
// Exported (not just used internally) so call sites without an active component
// instance — e.g. the app-creation hook that tags Clarity sessions — can still read
// UTM params off a pageContext they already have, without going through usePageContext().
export function getUtmParams(pageContext: PageContext): UtmParams {
  const fromUrl = readFromUrl(pageContext)

  if (typeof sessionStorage === 'undefined') return fromUrl

  if (Object.keys(fromUrl).length > 0) {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(fromUrl))
    return fromUrl
  }

  try {
    const stored = sessionStorage.getItem(STORAGE_KEY)
    return stored ? (JSON.parse(stored) as UtmParams) : {}
  } catch {
    return {}
  }
}

export function useUtmParams(): UtmParams {
  return getUtmParams(usePageContext())
}
